import { captureException } from '@/utils/track'
import { getCoIdAndUid } from '@/utils/token'

/** 本地缓存结构版本号，用于识别并淘汰旧结构数据。 */
const REQUEST_CACHE_VERSION = 2

/** 接口响应的最小结构约束。 */
interface RequestCacheResponse {
    success?: boolean
    error?: unknown
    [key: string]: unknown
}

/** 单个账号维度缓存条目。 */
interface RequestCacheStoreEntry<TResponse extends RequestCacheResponse = RequestCacheResponse> {
    response: TResponse
    cachedAt: number
}

/** cacheRequestWrap V2 本地缓存结构。 */
interface RequestCacheStore<TResponse extends RequestCacheResponse = RequestCacheResponse> {
    version: number
    entries: Record<string, RequestCacheStoreEntry<TResponse>>
}

/** 通用请求函数签名。 */
type RequestExecutor<TResponse extends RequestCacheResponse = RequestCacheResponse> = (
    ...args: unknown[]
) => Promise<TResponse>

/** 缓存包装配置。 */
interface CacheRequestWrapOptions<TResponse extends RequestCacheResponse = RequestCacheResponse> {
    onUpdate?: (response: TResponse) => void
}

/**
 * 创建空的 V2 缓存容器。
 * @returns {RequestCacheStore<TResponse>} 初始化后的缓存容器
 */
const createRequestCacheStore = <
    TResponse extends RequestCacheResponse = RequestCacheResponse,
>(): RequestCacheStore<TResponse> => {
    return {
        version: REQUEST_CACHE_VERSION,
        entries: {},
    }
}

/**
 * 读取并校验缓存；结构不匹配时按无缓存处理。
 * @param cacheKey 本地缓存 key
 * @returns {RequestCacheStore<TResponse>} 可用的缓存对象
 */
const getRequestCacheStore = <TResponse extends RequestCacheResponse = RequestCacheResponse>(
    cacheKey: string,
): RequestCacheStore<TResponse> => {
    const cacheStore = localStorage.getItem(cacheKey)
    if (!cacheStore) {
        return createRequestCacheStore<TResponse>()
    }

    try {
        const parsedStore = JSON.parse(cacheStore) as Partial<RequestCacheStore<TResponse>>
        if (
            parsedStore.version !== REQUEST_CACHE_VERSION ||
            !parsedStore.entries ||
            typeof parsedStore.entries !== 'object'
        ) {
            return createRequestCacheStore<TResponse>()
        }

        return {
            version: REQUEST_CACHE_VERSION,
            entries: parsedStore.entries as Record<string, RequestCacheStoreEntry<TResponse>>,
        }
    } catch (error) {
        console.error('cacheRequestWrap_parse_cache_failed', error)
        return createRequestCacheStore<TResponse>()
    }
}

/**
 * 持久化缓存对象。
 * @param cacheKey 本地缓存 key
 * @param cacheStore 缓存对象
 * @returns {void}
 */
const setRequestCacheStore = <TResponse extends RequestCacheResponse = RequestCacheResponse>(
    cacheKey: string,
    cacheStore: RequestCacheStore<TResponse>,
): void => {
    localStorage.setItem(cacheKey, JSON.stringify(cacheStore))
}

/**
 * 判断响应是否满足“成功且无 error”。
 * @param response 接口响应
 * @returns {boolean}
 */
const isResponseSuccessWithoutError = <TResponse extends RequestCacheResponse = RequestCacheResponse>(
    response: TResponse,
): boolean => {
    return Boolean(response?.success && !response?.error)
}

/**
 * 判断缓存是否发生变更（结构化对比）。
 * @param prev 缓存中的旧响应
 * @param next 新响应
 * @returns {boolean}
 */
const isCacheResponseChanged = <TResponse extends RequestCacheResponse = RequestCacheResponse>(
    prev: TResponse | undefined,
    next: TResponse,
): boolean => {
    if (prev === next) {
        return false
    }
    try {
        return JSON.stringify(prev ?? null) !== JSON.stringify(next ?? null)
    } catch (error) {
        console.error('cacheRequestWrap_compare_failed', error)
        return true
    }
}

/**
 * 更新账号维度缓存条目。
 * @param params 缓存写入参数
 * @returns {void}
 */
const updateRequestCacheStore = <TResponse extends RequestCacheResponse = RequestCacheResponse>({
    cacheKey,
    coidAndUid,
    response,
}: {
    cacheKey: string
    coidAndUid: string
    response: TResponse
}): void => {
    if (!coidAndUid) {
        return
    }
    const cacheStore = getRequestCacheStore<TResponse>(cacheKey)
    cacheStore.entries[coidAndUid] = {
        response,
        cachedAt: Date.now(),
    }
    setRequestCacheStore(cacheKey, cacheStore)
}

/**
 * 读取有效缓存（未过期）。
 * @param params 缓存读取参数
 * @returns {TResponse | null}
 */
const getValidRequestCacheResponse = <TResponse extends RequestCacheResponse = RequestCacheResponse>({
    cacheKey,
    coidAndUid,
}: {
    cacheKey: string
    coidAndUid: string
}): TResponse | null => {
    if (!coidAndUid) {
        return null
    }
    const cacheStore = getRequestCacheStore<TResponse>(cacheKey)
    const cacheEntry = cacheStore.entries[coidAndUid]
    if (!cacheEntry || typeof cacheEntry.cachedAt !== 'number') {
        return null
    }
    return cacheEntry.response || ({} as TResponse)
}

/**
 * 记录缓存包装场景下的接口异常埋点。
 * @param extraKey 缓存分组 key
 * @returns {void}
 */
const reportCacheRequestError = (extraKey: string): void => {
    captureException(extraKey + '请求错误', {
        anchor: 'cacheRequestWrap',
        mustNotify: false,
    })
}

/**
 * AI 生成（AI：Codex，模型：GPT-5-Codex，时间：2026-03-05 15:38）
 * 功能：包装接口请求实现 SWR 缓存，降低短时抖动对页面可用性的影响。
 * 关键步骤：先按 coId_uid 读取有效缓存；命中时立即返回并后台刷新；未命中/过期时直连接口；仅成功且无 error 才写回缓存。
 * @param fn 原始请求函数
 * @param extraKey 缓存分组 key，必填
 * @returns {(...args: unknown[]) => Promise<TResponse | Record<string, never>>} 与原请求函数一致的异步调用函数
 */
export const cacheRequestWrap = <TResponse extends RequestCacheResponse = RequestCacheResponse>(
    fn: RequestExecutor<TResponse>,
    extraKey: string,
    options: CacheRequestWrapOptions<TResponse> = {},
): ((...args: unknown[]) => Promise<TResponse | Record<string, never>>) => {
    return async (...args: unknown[]): Promise<TResponse | Record<string, never>> => {
        // 双奇中台没有返回coId和uid导致抛错登录不上，所以这里直接返回
        if (PROJECT_CONFIG.terminalName === 'OP') {
            return await fn(...args)
        }

        if (!extraKey) {
            throw new Error('extraKey 必填')
        }

        const { coId, uid } = getCoIdAndUid()
        const coidAndUid = coId && uid ? `${coId}_${uid}` : ''
        const cacheKey = `REQUEST_CACHE_${extraKey}`

        const cacheResponse = getValidRequestCacheResponse<TResponse>({
            cacheKey,
            coidAndUid,
        })
        if (cacheResponse) {
            fn(...args)
                .then(response => {
                    if (!isResponseSuccessWithoutError(response)) {
                        reportCacheRequestError(extraKey)
                        return
                    }

                    const cacheStore = getRequestCacheStore<TResponse>(cacheKey)
                    const prevResponse = cacheStore.entries[coidAndUid]?.response
                    const shouldNotify =
                        typeof options.onUpdate === 'function' && isCacheResponseChanged(prevResponse, response)

                    updateRequestCacheStore({
                        cacheKey,
                        coidAndUid,
                        response,
                    })
                    if (shouldNotify) {
                        try {
                            options.onUpdate?.(response)
                        } catch (error) {
                            console.error('cacheRequestWrap_onUpdate_failed', error)
                        }
                    }
                })
                .catch(error => {
                    if (error?.code !== PROJECT_CONFIG.ERROR_NO_LOGIN_CODE) {
                        reportCacheRequestError(extraKey)
                    }
                    console.error('cacheRequestWrap_background_refresh', error)
                })

            return cacheResponse
        }

        try {
            const response = await fn(...args)
            if (!isResponseSuccessWithoutError(response)) {
                reportCacheRequestError(extraKey)
                return {}
            }

            updateRequestCacheStore({
                cacheKey,
                coidAndUid,
                response,
            })
            return response
        } catch (error) {
            console.error('cacheRequestWrap_request_failed', error)
            if (error?.code !== PROJECT_CONFIG.ERROR_NO_LOGIN_CODE) {
                reportCacheRequestError(extraKey)
            }
            return {}
        }
    }
}
